import type { MenuDataItem } from '@ant-design/pro-components';
import type { RouteAccess } from '@/access';
import { PERMISSION_CONFIG, hasModulePermission } from '@/config/permissionConfig';

/**
 * Find the first accessible route based on user permissions
 * Returns the path of the first route the user has access to
 */
export function findFirstAccessibleRoute(
  routes: MenuDataItem[],
  access: RouteAccess,
  permissions: string[],
): string {
  // Default fallback route
  const defaultRoute = '/';

  // If no permissions, return default
  if (!permissions || permissions.length === 0) {
    return defaultRoute;
  }

  // Helper function to check if a route is accessible
  const isRouteAccessible = (route: MenuDataItem): boolean => {
    // Skip hidden routes
    if (route.hideInMenu) {
      return false;
    }

    // Check access requirement
    if (route.access) {
      const accessValue = access[route.access];

      if (typeof accessValue === 'boolean') {
        return accessValue;
      }

      if (route.access.endsWith('Module')) {
        const moduleKey = route.access as keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes;
        return hasModulePermission(moduleKey, permissions);
      }

      return false;
    }

    // Routes without access requirements are accessible
    return true;
  };

  const findRouteByPath = (items: MenuDataItem[], targetPath: string): MenuDataItem | null => {
    for (const item of items) {
      if (item.path === targetPath) {
        return item;
      }

      if (item.children) {
        const childMatch = findRouteByPath(item.children, targetPath);
        if (childMatch) {
          return childMatch;
        }
      }

      if (item.routes) {
        const routeMatch = findRouteByPath(item.routes, targetPath);
        if (routeMatch) {
          return routeMatch;
        }
      }
    }

    return null;
  };

  const findAccessible = (
    items: MenuDataItem[],
    redirectHistory: Set<string> = new Set(),
  ): string | null => {
    for (const item of items) {
      // Skip if not accessible
      if (!isRouteAccessible(item)) {
        continue;
      }

      // Check if this item has a direct path (not just a parent)
      if (item.path && !item.redirect && (item.component || item.element)) {
        return item.path;
      }

      // Check children
      if (item.children && item.children.length > 0) {
        const childPath = findAccessible(item.children, redirectHistory);
        if (childPath) {
          return childPath;
        }
      }

      // Check nested routes
      const nestedRoutes = item.routes as MenuDataItem[] | undefined;
      if (nestedRoutes?.length) {
        const routePath = findAccessible(nestedRoutes, redirectHistory);
        if (routePath) {
          return routePath;
        }
      }

      // If this is a redirect, attempt to resolve it safely
      if (item.redirect && typeof item.redirect === 'string') {
        if (redirectHistory.has(item.redirect)) {
          continue;
        }

        const updatedHistory = new Set(redirectHistory);
        updatedHistory.add(item.redirect);

        const targetRoute = findRouteByPath(routes, item.redirect);

        if (!targetRoute) {
          return item.redirect;
        }

        const resolvedPath = findAccessible([targetRoute], updatedHistory);
        if (resolvedPath) {
          return resolvedPath;
        }
      }
    }

    return null;
  };

  // Find first accessible route
  const firstAccessible = findAccessible(routes);

  return firstAccessible || defaultRoute;
}

const PRODUCT_MANAGEMENT_LANDING_PAGES: Array<{ path: string; access: string }> = [
  { path: '/product-management/products', access: 'canViewProducts' },
  { path: '/product-management/category-review', access: 'canViewCategoryReview' },
  { path: '/product-management/categories', access: 'canViewCategories' },
  { path: '/product-management/brands', access: 'canViewBrands' },
  { path: '/product-management/discounts', access: 'canViewDiscount' },
  { path: '/product-management/no-barcode-products', access: 'canViewNoBarcodeProducts' },
  { path: '/product-management/price-change-management', access: 'price_change:view' },
  {
    path: '/product-management/hanshow-electronic-shelf-labels',
    access: 'canViewHanshowElectronicShelfLabels',
  },
  { path: '/product-management/product-change-logs', access: 'canViewProductChangeLogs' },
  {
    path: '/product-management/expired-product-management',
    access: 'canViewExpiredProductManagement',
  },
  {
    path: '/product-management/unsalable-product-report',
    access: 'canViewUnsalableProductReport',
  },
  {
    path: '/product-management/product-expiry-date-errors',
    access: 'canViewProductExpiryDateErrors',
  },
  { path: '/product-management/supply-products', access: 'canViewSupplyProducts' },
  { path: '/product-management/featured-products', access: 'canViewFeaturedProducts' },
  { path: '/product-management/banned-products', access: 'canViewBannedProducts' },
  { path: '/product-management/product-stock-type', access: 'canManageProductStockType' },
  {
    path: '/product-management/carryover-product-management',
    access: 'canViewCarryoverProductManagement',
  },
  { path: '/product-management/shipment-specs', access: 'canViewShipmentSpec' },
  { path: '/product-management/product-displays', access: 'canViewProductDisplays' },
  {
    path: '/product-management/product-descriptions',
    access: 'canViewProductDescriptionManagement',
  },
];

export function getAccessibleProductManagementLandingPage(access: RouteAccess): string | null {
  for (const page of PRODUCT_MANAGEMENT_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

const ONLINE_STORE_LANDING_PAGES: Array<{ path: string; access: string }> = [
  {
    path: '/online-store-management/products',
    access: 'canViewOnlineProducts',
  },
  {
    path: '/online-store-management/orders',
    access: 'canViewOnlineOrders',
  },
  {
    path: '/online-store-management/order-followup',
    access: 'canViewOnlineOrderFollowup',
  },
  {
    path: '/online-store-management/analytics',
    access: 'canViewOnlineStoreBehaviour',
  },
  {
    path: '/online-store-management/discount-codes',
    access: 'canViewOnlineStoreDiscountCodes',
  },
  {
    path: '/online-store-management/customer-messages',
    access: 'canViewOnlineStoreCustomerMessages',
  },
  {
    path: '/online-store-management/blog-posts',
    access: 'canViewOnlineStoreBlogPosts',
  },
];

export function getAccessibleOnlineStoreLandingPage(access: RouteAccess): string | null {
  for (const page of ONLINE_STORE_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

const HR_MANAGEMENT_LANDING_PAGES: Array<{ path: string; access: string }> = [
  {
    path: '/hr-management/dashboard',
    access: 'canViewHrDashboard',
  },
  {
    path: '/hr-management/employees',
    access: 'canViewHrEmployees',
  },
  {
    path: '/hr-management/roster-schedule',
    access: 'canViewHrRoster',
  },
  {
    path: '/hr-management/attendance-stats',
    access: 'canViewHrAttendanceStats',
  },
  {
    path: '/hr-management/work-hour-anomalies',
    access: 'canViewHrWorkHourAnomalies',
  },
  {
    path: '/hr-management/leave-management',
    access: 'canViewHrLeave',
  },
  {
    path: '/hr-management/salaries',
    access: 'canViewHrSalaries',
  },
  {
    path: '/hr-management/salary-visibility-management',
    access: 'canViewHrSalaryVisibility',
  },
];

export function getAccessibleHrManagementLandingPage(access: RouteAccess): string | null {
  for (const page of HR_MANAGEMENT_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

const SUPPLIER_MANAGEMENT_LANDING_PAGES: Array<{ path: string; access: string }> = [
  { path: '/supplier-management/follow-up', access: 'canViewSupplierFollowUp' },
  { path: '/supplier-management/supplier-development', access: 'canViewSupplierDevelopment' },
  { path: '/supplier-management/suppliers', access: 'canViewSuppliers' },
  { path: '/supplier-management/supplier-groups', access: 'canViewSupplierPlatformGroups' },
  { path: '/supplier-management/supplier-groups', access: 'canViewSupplierPlatformMessages' },
  { path: '/supplier-management/calls', access: 'canViewSupplierCalls' },
];

export function getAccessibleSupplierManagementLandingPage(access: RouteAccess): string | null {
  for (const page of SUPPLIER_MANAGEMENT_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

const STORE_MANAGEMENT_LANDING_PAGES: Array<{ path: string; access: string }> = [
  { path: '/store-management/locations', access: 'canViewStores' },
  { path: '/store-management/location-rent', access: 'canViewStoreRent' },
  { path: '/finance/rent', access: 'canViewStoreRent' },
  { path: '/store-management/store-targets', access: 'canViewStoreTargets' },
  { path: '/store-management/category-placement', access: 'canViewCategoryPlacement' },
  { path: '/store-management/pda-operation-logs', access: 'canViewPdaOperationLogs' },
  { path: '/store-management/pda-accounts', access: 'canViewPdaAccount' },
];

export function getAccessibleStoreManagementLandingPage(access: RouteAccess): string | null {
  for (const page of STORE_MANAGEMENT_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

const POS_SYSTEM_MANAGEMENT_LANDING_PAGES: Array<{ path: string; access: string }> = [
  { path: '/pos-system-management/location-accounts', access: 'canViewStoreAccount' },
  { path: '/pos-system-management/payment-gateways', access: 'canViewPaymentMethod' },
  { path: '/pos-system-management/pos-app-versions', access: 'canViewPosAppVersion' },
  { path: '/pos-system-management/pos-sessions', access: 'canViewPosSession' },
  { path: '/pos-system-management/operation-logs', access: 'canViewPosOperationLogs' },
  { path: '/pos-system-management/quick-actions', access: 'canViewPosQuickActions' },
  { path: '/pos-system-management/terminal-config-management', access: 'canViewTerminalConfig' },
];

export function getAccessiblePosSystemManagementLandingPage(access: RouteAccess): string | null {
  for (const page of POS_SYSTEM_MANAGEMENT_LANDING_PAGES) {
    if (access[page.access]) {
      return page.path;
    }
  }

  return null;
}

/**
 * Get a prioritized list of common landing pages
 * This helps direct users to the most relevant page based on their permissions
 */
export function getAccessibleLandingPage(access: RouteAccess, permissions: string[]): string {
  const productManagementLandingPage = getAccessibleProductManagementLandingPage(access);
  if (productManagementLandingPage) {
    return productManagementLandingPage;
  }

  const onlineStoreLandingPage = getAccessibleOnlineStoreLandingPage(access);
  if (onlineStoreLandingPage) {
    return onlineStoreLandingPage;
  }

  const hrManagementLandingPage = getAccessibleHrManagementLandingPage(access);
  if (hrManagementLandingPage) {
    return hrManagementLandingPage;
  }

  // Priority order of landing pages
  const primaryLandingPages = [
    { path: '/procurement-analysis/dashboard', access: 'canViewProcurementDashboard' },
    { path: '/purchase-order-management/opportunities', access: 'purchaseModule' },
  ];

  for (const page of primaryLandingPages) {
    const accessValue = access[page.access];

    if (typeof accessValue === 'boolean') {
      if (accessValue) {
        return page.path;
      }
      continue;
    }

    if (page.access.endsWith('Module')) {
      const moduleKey = page.access as keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes;
      if (hasModulePermission(moduleKey, permissions)) {
        return page.path;
      }
    }
  }

  const supplierManagementLandingPage = getAccessibleSupplierManagementLandingPage(access);
  if (supplierManagementLandingPage) {
    return supplierManagementLandingPage;
  }

  const storeManagementLandingPage = getAccessibleStoreManagementLandingPage(access);
  if (storeManagementLandingPage) {
    return storeManagementLandingPage;
  }

  const posSystemManagementLandingPage = getAccessiblePosSystemManagementLandingPage(access);
  if (posSystemManagementLandingPage) {
    return posSystemManagementLandingPage;
  }

  const landingPages = [
    { path: '/inventory-management/overview', access: 'inventoryModule' },
    { path: '/sales-analysis/dashboard', access: 'canViewDataDashboard' },
    {
      path: '/sales-analysis/franchise-store-sales-analysis',
      access: 'canViewFranchiseStoreSalesAnalysis',
    },
    { path: '/market-info/collection-tasks', access: 'marketInfoModule' },
    { path: '/user-management/users', access: 'userManagementModule' },
    { path: '/pos-system-management/location-accounts', access: 'posModule' },
    { path: '/warehouse-transfer/dashboard', access: 'canViewWarehouseDashboard' },
    { path: '/warehouse-transfer/list', access: 'canViewTransferOrder' },
    { path: '/warehouse-transfer/pda-operation-logs', access: 'canViewPdaShippingLogs' },
    {
      path: '/inventory-management/anomaly-dashboard',
      access: 'canViewInventoryAnomalyDashboard',
    },
    {
      path: '/warehouse-transfer/exception-followup',
      access: 'canViewTransferExceptionFollowup',
    },
    { path: '/user-management/franchisee-accounts', access: 'franchiseeManagementModule' },
    { path: '/user-management/warehouse-users', access: 'canViewWarehouseUsers' },
    { path: '/user-management/wholesale-customers', access: 'canViewWholesaleCustomers' },
  ];

  // Find first accessible landing page
  for (const page of landingPages) {
    const accessValue = access[page.access];

    if (typeof accessValue === 'boolean') {
      if (accessValue) {
        return page.path;
      }
      continue;
    }

    if (page.access.endsWith('Module')) {
      const moduleKey = page.access as keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes;
      if (hasModulePermission(moduleKey, permissions)) {
        return page.path;
      }
    }
  }

  // If no specific module access, return root
  return '/';
}
